/**
 * useCostSummary Hook
 *
 * Provides cost summary data and trends for financial analysis.
 * Calculates total costs, cost per kg, gross margins, and trend data
 * over multiple periods.
 *
 * Part of US-P7-001: Cost Tracking for Financial Intelligence
 *
 * @example
 * // Get summary for a date range
 * const {
 *   summary,
 *   trend,
 *   costPerKg,
 *   grossMargin,
 *   isLoading,
 *   error,
 *   refresh
 * } = useCostSummary('2025-01-01', '2025-01-31')
 *
 * @example
 * // Get trend data (summary will be null)
 * const {
 *   trend,
 *   isLoading,
 *   error
 * } = useCostSummary(null, null, { trendMonths: 12 })
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'

// =============================================================================
// Constants
// =============================================================================

const FUNCTION_NAME = 'cost-tracking'

// =============================================================================
// Edge Function Helper
// =============================================================================

/**
 * Call the cost-tracking Edge Function with REST-style routing
 *
 * @param {string} path - The endpoint path
 * @param {Object} options - Fetch options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.body] - Request body (for POST)
 * @param {URLSearchParams} [options.params] - Query parameters (for GET)
 * @returns {Promise<Object>} Response data
 * @throws {Error} On network or API error
 */
async function callCostTrackingFunction(path, options = {}) {
  const { method = 'GET', body, params } = options

  let fullPath = `${FUNCTION_NAME}/${path}`
  if (params) {
    fullPath += `?${params.toString()}`
  }

  const invokeOptions = { method }
  if (body) {
    invokeOptions.body = body
  }

  const { data, error } = await supabase.functions.invoke(fullPath, invokeOptions)

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  // Check for API-level errors in response
  if (data?.error) {
    const err = new Error(data.message || data.error)
    err.retryable = data.retryable ?? false
    throw err
  }

  return data
}

// =============================================================================
// Main Hook: useCostSummary
// =============================================================================

/**
 * Hook to fetch cost summary and trend data
 *
 * @param {string|null} periodStart - Start date in YYYY-MM-DD format (required for summary)
 * @param {string|null} periodEnd - End date in YYYY-MM-DD format (required for summary)
 * @param {Object} [options] - Additional options
 * @param {number} [options.trendMonths] - Number of months for trend data (1-60)
 * @returns {Object} Hook state and actions
 * @property {Object|null} summary - Cost summary for the period
 * @property {Array|null} trend - Trend data points (if trendMonths specified)
 * @property {number|null} costPerKg - Cost per kg from summary
 * @property {number|null} grossMargin - Gross margin from summary
 * @property {number|null} grossMarginPercent - Gross margin percentage from summary
 * @property {boolean} isLoading - True while loading data
 * @property {string|null} error - Error message if any
 * @property {Function} refresh - Refetch data
 */
export function useCostSummary(periodStart, periodEnd, options = {}) {
  const { trendMonths = null } = options

  // Data state
  const [summary, setSummary] = useState(null)
  const [trend, setTrend] = useState(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const [loadingTrend, setLoadingTrend] = useState(false)
  const [error, setError] = useState(null)

  // Combined loading state
  const isLoading = loadingSummary || loadingTrend

  // Derived metrics from summary
  const costPerKg = useMemo(() => {
    return summary?.metrics?.cost_per_kg ?? null
  }, [summary])

  const grossMargin = useMemo(() => {
    return summary?.metrics?.gross_margin ?? null
  }, [summary])

  const grossMarginPercent = useMemo(() => {
    return summary?.metrics?.gross_margin_percent ?? null
  }, [summary])

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  /**
   * Fetch cost summary for the date range
   */
  const fetchSummary = useCallback(async () => {
    // Only fetch summary if both dates are provided
    if (!periodStart || !periodEnd) {
      setSummary(null)
      setLoadingSummary(false)
      return
    }

    // Validate date range
    if (new Date(periodStart) > new Date(periodEnd)) {
      setError('Start date must be before or equal to end date')
      setSummary(null)
      setLoadingSummary(false)
      return
    }

    setLoadingSummary(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        start: periodStart,
        end: periodEnd
      })

      const response = await callCostTrackingFunction('summary', { params })
      setSummary(response)
    } catch (err) {
      console.error('[useCostSummary] Error fetching summary:', err)
      setError(err.message)
      setSummary(null)
    } finally {
      setLoadingSummary(false)
    }
  }, [periodStart, periodEnd])

  /**
   * Fetch trend data for multiple months
   */
  const fetchTrend = useCallback(async () => {
    // Only fetch trend if trendMonths is specified
    if (!trendMonths) {
      setTrend(null)
      setLoadingTrend(false)
      return
    }

    // Validate trendMonths
    if (typeof trendMonths !== 'number' || trendMonths < 1 || trendMonths > 60) {
      setError('trendMonths must be a number between 1 and 60')
      setTrend(null)
      setLoadingTrend(false)
      return
    }

    setLoadingTrend(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        months: trendMonths.toString()
      })

      const response = await callCostTrackingFunction('summary/trend', { params })
      setTrend(response.data || [])
    } catch (err) {
      console.error('[useCostSummary] Error fetching trend:', err)
      setError(err.message)
      setTrend(null)
    } finally {
      setLoadingTrend(false)
    }
  }, [trendMonths])

  // ==========================================================================
  // Actions
  // ==========================================================================

  /**
   * Refresh all data (refetch summary and trend)
   */
  const refresh = useCallback(async () => {
    setError(null)
    await Promise.all([
      fetchSummary(),
      fetchTrend()
    ])
  }, [fetchSummary, fetchTrend])

  // ==========================================================================
  // Effects
  // ==========================================================================

  // Fetch summary when dates change
  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  // Fetch trend when trendMonths changes
  useEffect(() => {
    fetchTrend()
  }, [fetchTrend])

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // Data
    summary,
    trend,

    // Derived metrics
    costPerKg,
    grossMargin,
    grossMarginPercent,

    // Status
    isLoading,
    error,

    // Actions
    refresh
  }
}

// =============================================================================
// Helper Hook: useCostTrend
// =============================================================================

/**
 * Simplified hook to fetch only trend data
 *
 * @param {number} months - Number of months for trend (1-60)
 * @returns {Object} Hook state
 * @property {Array|null} trend - Array of trend data points
 * @property {Object|null} period - Period info (months, start, end)
 * @property {boolean} isLoading - True while loading
 * @property {string|null} error - Error message if any
 * @property {Function} refresh - Refetch trend data
 */
export function useCostTrend(months = 12) {
  const [trend, setTrend] = useState(null)
  const [period, setPeriod] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTrend = useCallback(async () => {
    // Validate months
    if (typeof months !== 'number' || months < 1 || months > 60) {
      setError('months must be a number between 1 and 60')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        months: months.toString()
      })

      const response = await callCostTrackingFunction('summary/trend', { params })
      setTrend(response.data || [])
      setPeriod(response.period || null)
    } catch (err) {
      console.error('[useCostTrend] Error fetching trend:', err)
      setError(err.message)
      setTrend(null)
      setPeriod(null)
    } finally {
      setLoading(false)
    }
  }, [months])

  useEffect(() => {
    fetchTrend()
  }, [fetchTrend])

  return {
    trend,
    period,
    isLoading: loading,
    error,
    refresh: fetchTrend
  }
}

// =============================================================================
// Default Export
// =============================================================================

export default useCostSummary
